import axios from "axios";

interface IComplexityProof {
    length: number;
    hasNumber: boolean;
    hasUpper: boolean;
    hasSymbol: boolean;
}

export interface IResetPasswordPayload {
    newPasswordSha256: string;
    newKeyEncrypted: string;
    newKeyResearcherInvestigation: string;
    complexityProof: IComplexityProof;
}

export interface ISelfChangePasswordPayload {
    oldPasswordSha256: string;
    newPasswordSha256: string;
    newKeyEncrypted: string;
    complexityProof: IComplexityProof;
}

interface ISimpleResponse {
    status: number;
    errorMessage?: string;
}

interface ISelfChangeResponse extends ISimpleResponse {
    jwt?: string;
}

const apiBase = () => import.meta.env.VITE_APP_API_URL;
const authHeader = () => ({ headers: { Authorization: localStorage.getItem("jwt") } });

/** SHER-XYZ — re-auth helper for sensitive flows. POST { password } returns 200/401. */
export function verifyOwnPasswordService(passwordSha256: string): Promise<ISimpleResponse> {
    return new Promise((resolve, reject) => {
        axios.post(`${apiBase()}/researcher/verify-password`, { password: passwordSha256 }, authHeader())
            .then(response => {
                if (response.status === 200) resolve(response.data);
                else reject(response.data);
            })
            .catch(err => reject(err));
    });
}

/** SHER-XYZ — admin resets another researcher's password within an investigation. */
export function resetResearcherPasswordService(
    uuidInvestigation: string,
    uuidTargetResearcher: string,
    payload: IResetPasswordPayload,
): Promise<ISimpleResponse> {
    return new Promise((resolve, reject) => {
        axios.post(
            `${apiBase()}/researcher/investigation/${uuidInvestigation}/reset-password/${uuidTargetResearcher}`,
            payload,
            authHeader(),
        )
            .then(response => {
                if (response.status === 200) resolve(response.data);
                else reject(response.data);
            })
            .catch(err => reject(err));
    });
}

/** SHER-XYZ — self-service password change. Returns a fresh JWT to swap into localStorage. */
export function selfChangePasswordService(payload: ISelfChangePasswordPayload): Promise<ISelfChangeResponse> {
    return new Promise((resolve, reject) => {
        axios.post(`${apiBase()}/researcher/self-change-password`, payload, authHeader())
            .then(response => {
                if (response.status === 200) resolve(response.data);
                else reject(response.data);
            })
            .catch(err => reject(err));
    });
}

/**
 * SHER-XYZ — client-side password complexity check.
 * Same rules enforced server-side: ≥ 8 chars, ≥ 1 number, ≥ 1 uppercase, ≥ 1 symbol.
 * Returns the proof object that must be sent with reset / self-change requests.
 */
export function evaluatePasswordComplexity(password: string): IComplexityProof {
    return {
        length: password.length,
        hasNumber: /\d/.test(password),
        hasUpper: /[A-Z]/.test(password),
        hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
    };
}

export function isPasswordComplexEnough(password: string): boolean {
    const proof = evaluatePasswordComplexity(password);
    return proof.length >= 8 && proof.hasNumber && proof.hasUpper && proof.hasSymbol;
}
